import { FC, useRef } from 'react'
import { ItemRendererProps } from 'react-calendar-timeline/dist/lib/items/Item'
import { Trans } from 'react-i18next'

import { lighten, Typography, useTheme } from '@mui/material'

import { GRTooltip } from '../../../../../../components/GRTooltip/GRTooltip'
import { TrendIndicator } from '../../../../../../components/TrendIndicator/TrendIndicator'
import { PerformanceEffectTimelineItem } from '../../../../types/LiveEvents'
import { PerformanceEffectType } from '../../../../types/PerformanceEffect'

// prettier-ignore
export const LiveEventsCalendarPerformanceEffectItem: FC<ItemRendererProps<PerformanceEffectTimelineItem>> = ({ item, itemContext, getItemProps, getResizeProps }) => {
  const theme = useTheme()
  const itemRef = useRef(null)
  const { left: leftResizeProps, right: rightResizeProps } = getResizeProps()
  const severity = item.performanceEffect.value >= 0 ? 'success' : 'warning'
  const backgroundColor = item.performanceEffect.value >= 0 ? theme.palette.success.main : theme.palette.warning.main
  const borderColor = item.performanceEffect.value >= 0 ? theme.palette.success.main : theme.palette.warning.main
  // Always show the directional arrow when the change is ±Infinity (recovered
  // from a 0 → non-zero transition in the daily values). The numeric value is
  // empty in that case, so the arrow alone communicates direction even in the
  // narrowest strip.
  const isInfiniteChange = !isFinite(item.performanceEffect.value)
  const showDirectionArrow = itemContext.dimensions.width > 60 || isInfiniteChange
  const showValueSign = itemContext.dimensions.width > 35

  return (
    <GRTooltip
      content={<TooltipContent itemType={item.group as PerformanceEffectType} value={item.performanceEffect.value} />}
      severity={severity}
      anchorRef={itemRef}
    >
      <div
        {...getItemProps({
          style: {
            background: lighten(backgroundColor, 0.9),
            color: 'white',
            borderTopWidth: 1,
            borderRightWidth: 1,
            borderBottomWidth: 1,
            borderLeftWidth: 1,
            borderStyle: 'solid',
            borderColor: borderColor,
            borderRadius: 10,
            overflow: 'hidden',
          },
        })}
        ref={itemRef}
      >
        {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : null}

        <div
          style={{
            height: itemContext.dimensions.height,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            position: 'relative',
            bottom: '2px',
            textAlign: 'center',
          }}
        >
          <TrendIndicator value={item.performanceEffect.value} sign={showValueSign} direction={showDirectionArrow} allowInfinity />
        </div>

        {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : null}
      </div>
    </GRTooltip>
  )
}

const TooltipContent: FC<{ itemType: PerformanceEffectType; value: number }> = ({ itemType, value }) => {
  return (
    <>
      <Typography variant="body1" mb={1}>
        <Trans i18nKey={`live-events:performance_effect_type_${itemType.toLowerCase()}`} />
      </Typography>
      <Typography variant="body1" textAlign="center" mr={2}>
        <TrendIndicator value={value} sign direction allowInfinity />
      </Typography>
    </>
  )
}
